import { useIntl } from 'react-intl';
import { TitledPaperWithSaveBar } from '../../../modules/components/titledPaper/titledPaperWithSaveBar/titledPaperWithSaveBar';
import SuccessIcon from '@mui/icons-material/CheckCircleOutline';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import WarningIcon from '@mui/icons-material/ReportGmailerrorredOutlined';
import NotRunIcon from '@mui/icons-material/DoNotDisturbOnOutlined';
import { PolicyRule } from '../../../model/interfaces/policyRule';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { ScrollableStack } from '../../../modules/components/scrollableStack';
import { useEffect } from 'react';
import { Stack, TextField, Checkbox, FormControlLabel, useTheme } from '@mui/material';
import { XmlField } from '../../../modules/formikFields/xmlField';
import { customTheme } from '../../../styles/defaultTheme';

interface RuleEditorProps {
  rule: PolicyRule;
  setHasUnsavedChanges?: (hasUnsavedChanges: boolean) => void;
  onSave: (rule: PolicyRule) => void;
  onDelete: (rule: PolicyRule) => void;
}

export const RuleEditor = (props: RuleEditorProps) => {
  const { rule, setHasUnsavedChanges, onSave, onDelete } = props;
  const intl = useIntl();
  const colorTheme = customTheme;
  const theme = useTheme();

  const notEmptyFields = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    description: Yup.string().required('Description is Required'),
    expression1: Yup.string().required('Success expression is required'),
    expression2: Yup.string().required('Warning expression is required'),
    criteria: Yup.string().required('Criteria is required')
  });

  return (
    <Formik
      initialValues={{ ...rule, sortOrder: rule.sortOrder ?? '' }}
      onSubmit={(values, { setSubmitting }) => {
        onSave({
          ...values,
          sortOrder: values.sortOrder === '' ? null : Number(values.sortOrder)
        });
        setSubmitting(false);
      }}
      validationSchema={notEmptyFields}
      enableReinitialize>
      {({ errors, touched, dirty }) => (
        <>
          {setHasUnsavedChanges && (
            <DirtTagger dirty={dirty} onTag={(dirty) => setHasUnsavedChanges(dirty)} />
          )}
          <TitledPaperWithSaveBar
            color={colorTheme.palette.background.default}
            columnId="RuleEditor"
            title={intl.formatMessage({
              id: 'RuleEditor'
            })}
            onClickDelete={() => onDelete(rule)}
            changeDate={rule.changedDate}>
            <Form style={{ display: 'contents' }}>
              <Stack spacing={1} padding={1}>
                <Field
                  name={'name'}
                  label={intl.formatMessage({
                    id: 'RuleName'
                  })}
                  multiline
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  as={TextField}
                />
                <Field
                  name={'description'}
                  label={intl.formatMessage({
                    id: 'RuleDescription'
                  })}
                  multiline
                  error={touched.description && Boolean(errors.description)}
                  helperText={touched.description && errors.description}
                  as={TextField}
                />
                <Stack alignItems={'center'} spacing={1} direction="row">
                  <Field
                    name={'sortOrder'}
                    label={intl.formatMessage({
                      id: 'RuleSortOrder'
                    })}
                    type="number"
                    as={TextField}
                  />
                  <FormControlLabel
                    control={<Field name={'hidden'} type="checkbox" as={Checkbox} />}
                    label={intl.formatMessage({
                      id: 'Hidden'
                    })}
                  />
                </Stack>
              </Stack>

              <hr
                style={{
                  marginBottom: 0,
                  width: '100%' /* For some reason this first <hr /> doesn't have full width */
                }}
              />
              <ScrollableStack>
                <Stack spacing={1}>
                  <XmlField
                    name="message"
                    icon={<InfoIcon color="info" />}
                    intlLabelId="RuleMessage"
                  />

                  <hr />
                  <XmlField
                    name="expression1"
                    icon={<SuccessIcon color="success" />}
                    intlLabelId="RuleSuccess"
                    variant="expression"
                  />

                  <hr />
                  <XmlField
                    name="expression2"
                    icon={<WarningIcon color="warning" />}
                    intlLabelId="RuleWarning"
                    variant="expression"
                  />

                  <hr />
                  <XmlField
                    name="criteria"
                    icon={<NotRunIcon color="primary" />}
                    intlLabelId="RuleCriteria"
                    variant="expression"
                  />
                </Stack>
              </ScrollableStack>
              <hr
                style={{
                  marginTop: 0,
                  width: '100%' /* For some reason this first <hr /> doesn't have full width */
                }}
              />
            </Form>
          </TitledPaperWithSaveBar>
        </>
      )}
    </Formik>
  );
};

const DirtTagger = ({ dirty, onTag }: { dirty: boolean; onTag: (dirty: boolean) => void }) => {
  useEffect(() => {
    onTag(dirty);
  }, [dirty, onTag]);
  return null;
};
